// src/pages/Welcome/styles.js
import { COLORS, LAYOUT, TYPOGRAPHY, BUTTON_STYLES, LOADING_STYLES } from '../../constants';

export const welcomeStyles = {
  // Main container - Full viewport utilization
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.white,
    position: 'relative',
    overflow: 'hidden',
  },

  // Hero Section - Modern gradient background with ISU colors
  hero: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: `0 ${LAYOUT.containerPadding}`,
  },

  // Decorative background elements
  heroBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, rgba(249, 208, 22, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },

  heroContent: {
    maxWidth: '1200px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    zIndex: 2,
  },

  heroTitle: {
    fontSize: 'clamp(3rem, 6vw, 5rem)',
    fontWeight: '800',
    color: COLORS.white,
    marginBottom: LAYOUT.spacing.xl,
    lineHeight: '1.1',
    letterSpacing: '-0.02em',
  },

  heroSubtitle: {
    fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: LAYOUT.spacing.huge,
    lineHeight: 1.6,
    maxWidth: '600px',
    margin: `0 auto ${LAYOUT.spacing.huge} auto`,
  },

  authButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: LAYOUT.spacing.lg,
    flexWrap: 'wrap',
    marginBottom: LAYOUT.spacing.xxxl,
  },

  // Modern CTA buttons with consistent ISU branding
  primaryButton: {
    ...BUTTON_STYLES.primary,
    background: COLORS.primary,
    color: COLORS.white,
    padding: '18px 36px',
    fontSize: '18px',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(206, 17, 38, 0.4)',
    minWidth: '180px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 30px rgba(206, 17, 38, 0.5)',
      backgroundColor: COLORS.primaryDark,
    },
  },

  // White background sign in button
  signInButton: {
    background: COLORS.white,
    color: COLORS.primary,
    padding: '18px 36px',
    fontSize: '18px',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '180px',
    outline: 'none',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      transform: 'translateY(-2px)',
      background: COLORS.grayLight,
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
    },
  },

  secondaryButton: {
    ...BUTTON_STYLES.secondary,
    background: 'transparent',
    color: COLORS.white,
    border: `2px solid rgba(255, 255, 255, 0.8)`,
    padding: '18px 36px',
    fontSize: '18px',
    fontWeight: '600',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '180px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderColor: COLORS.white,
    },
  },

  // Features section
  featuresSection: {
    padding: `${LAYOUT.spacing.huge} ${LAYOUT.containerPadding}`,
    backgroundColor: COLORS.grayLight,
  },

  featuresContent: {
    maxWidth: LAYOUT.maxWidth,
    margin: '0 auto',
  },

  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: LAYOUT.spacing.xxxl,
    marginTop: LAYOUT.spacing.xxxl,
  },

  featureCard: {
    textAlign: 'center',
    padding: LAYOUT.spacing.xl,
  },

  featureIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: COLORS.primary,
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 auto ${LAYOUT.spacing.lg} auto`,
    boxShadow: `0 8px 24px ${COLORS.shadow}`,
    color: 'white',
  },

  featureTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
    fontSize: '1.5rem',
  },

  featureDescription: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    lineHeight: 1.7,
    fontSize: '1.1rem',
  },

  // Marketplace Section
  marketplace: {
    padding: `${LAYOUT.spacing.huge} ${LAYOUT.containerPadding}`,
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    position: 'relative',
  },

  marketplaceContent: {
    maxWidth: LAYOUT.maxWidth,
    margin: '0 auto',
  },

  sectionTitle: {
    ...TYPOGRAPHY.heading2,
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xl,
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    color: COLORS.text.primary,
  },

  sectionSubtitle: {
    fontSize: '1.25rem',
    color: COLORS.text.secondary,
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xxxl,
    maxWidth: '600px',
    margin: `0 auto ${LAYOUT.spacing.xxxl} auto`,
  },

  filtersContainer: {
    marginBottom: LAYOUT.spacing.xxxl,
  },

  // Modern listings grid with better spacing
  listingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: LAYOUT.spacing.xl,
  },

  // Enhanced empty state
  emptyState: {
    textAlign: 'center',
    padding: LAYOUT.spacing.huge,
    backgroundColor: COLORS.white,
    borderRadius: '20px',
    margin: LAYOUT.spacing.xl,
    border: `2px solid ${COLORS.primary}15`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  },

  emptyTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.lg,
    fontSize: '2rem',
  },

  emptyText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    maxWidth: '500px',
    margin: '0 auto',
    fontSize: '1.1rem',
    lineHeight: 1.6,
  },

  // Enhanced loading state
  loadingContainer: {
    ...LOADING_STYLES.container,
    minHeight: '400px',
    backgroundColor: COLORS.white,
    borderRadius: '20px',
    margin: LAYOUT.spacing.xl,
    border: `2px solid ${COLORS.primary}15`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  },

  loadingSpinner: {
    ...LOADING_STYLES.spinner,
    width: '60px',
    height: '60px',
    borderWidth: '4px',
    borderColor: `${COLORS.primary}20`,
    borderTopColor: COLORS.primary,
  },

  loadingText: {
    ...LOADING_STYLES.text,
    fontSize: '1.1rem',
  },

  // Responsive design
  '@media (max-width: 768px)': {
    heroTitle: {
      fontSize: '2.5rem',
    },
    heroSubtitle: {
      fontSize: '1.1rem',
    },
    authButtons: {
      flexDirection: 'column',
      alignItems: 'center',
    },
    featuresGrid: {
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.xl,
    },
    listingsGrid: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
  },
};